import { Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { filterSampleEntryFields } from '../../../../../../../../../../shared/helpers/sample-collection';
import { batchTicketData, batchTicketLoading } from '../../../../../../../../core/projects.selectors';
import { ScanBatchTicketRequest, ClearBatchTicketData } from '../../../../../../../../core/projects.actions';
import { ExpressionEvaluatorService } from '../../../../../../../../../../shared/services/expression-evaluator.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';
import {
  AcceptanceRecordEntry,
  acceptanceRecordHasData,
  createEmptyAcceptanceRecord,
  parseAcceptanceGroupValues,
} from '../acceptance-record.utils';

interface FieldValue {
  id: string;
  value: any;
}

@Component({
  standalone: false,
  selector: 'app-item-postings-acceptance-record-dialog',
  templateUrl: './item-postings-acceptance-record-dialog.component.html',
  styleUrls: ['./item-postings-acceptance-record-dialog.component.scss'],
})
export class ItemPostingsAcceptanceRecordDialogComponent implements OnInit, OnDestroy {
  bidItemName = '';
  materials: any[] = [];
  projectId: string = '';
  dailyReportId: number | string = null;
  bidItemId: number | string = null;
  bidItemMaterialSetId: number | string = null;
  sampleRecordsEnabled = false;
  acceptanceFields: any[] = [];
  expandedMaterialId: string = null;

  // formData: { [bid_item_material_set_material_id]: AcceptanceRecordEntry[] }
  // Each entry is { uuid, acceptance_record: FieldValue[], groups: FieldValue[] }.
  formData: { [key: string]: AcceptanceRecordEntry[] } = {};

  // group_values keys that are not acceptance-record related (e.g. sample
  // record data) — preserved untouched on save. Keyed by msmId.
  passthroughGroupValues: { [key: string]: { [key: string]: any } } = {};

  // Forms for acceptance record fields, per material per record
  acceptanceForms: { [materialSetMaterialId: string]: { [recordUuid: string]: FormGroup } } = {};

  // Forms for concrete group fields, per material per record
  groupForms: { [materialSetMaterialId: string]: { [recordUuid: string]: { [fieldId: string]: FormGroup } } } = {};

  // Concrete groups per material (from material.groups)
  materialGroups: { [materialId: string]: any[] } = {};

  // Expanded concrete group sections, keyed by record uuid
  expandedGroupSections: { [recordUuid: string]: { [groupIndex: number]: string } } = {};

  projectLookups: any = {};
  batchTicketScanning = false;
  scannedFile: { name: string; fieldsAutofilled: number; recordUuid: string } = null;
  // Autofilled section keys as `${recordUuid}:${sectionName}`
  autofilledSections: Set<string> = new Set();
  @ViewChild('batchTicketFileInput') batchTicketFileInput: ElementRef<HTMLInputElement>;
  private batchTicketTarget: { msmId: string; recordUuid: string } = null;
  private appData: any = {};
  private pickListOptionsCache: { [fieldId: string]: any[] } = {};
  private readonly onDestroy: Subject<any> = new Subject<any>;

  constructor(
    public dialogRef: MatDialogRef<ItemPostingsAcceptanceRecordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<fromRoot.State>,
    private dialog: MatDialog,
    private exprEvalService: ExpressionEvaluatorService,
    private elRef: ElementRef,
  ) {
    this.bidItemName = data?.bidItemName || '';
    this.materials = data?.materials || [];
    this.projectLookups = data?.projectLookups || {};
    this.projectId = data?.projectId || '';
    this.dailyReportId = data?.dailyReportId ?? null;
    this.bidItemId = data?.bidItemId ?? null;
    this.bidItemMaterialSetId = data?.bidItemMaterialSetId ?? null;
    this.sampleRecordsEnabled = !!data?.sampleRecordsEnabled;
  }

  ngOnInit() {
    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(appData => {
        this.appData = appData;
        // sample_type / source_id only exist to launch "+ Create sample";
        // hide them when sample collection is off for this project.
        this.acceptanceFields = filterSampleEntryFields(
          appData?.dailyReportFields?.material_acceptance?.fields || [],
          this.sampleRecordsEnabled
        );
        this.initFormData();
      });

    this.store.select(batchTicketLoading)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(loading => {
        this.batchTicketScanning = loading;
      });

    this.store.select(batchTicketData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.populateExtractedData(data);
        }
      });
  }

  private initFormData() {
    this.materials.forEach((material, index) => {
      const msmId = material.bid_item_material_set_material_id || material.bid_item_material_set_material?.id;
      if (!msmId) return;

      // Normalize existing group_values (legacy single-record shape becomes
      // record 1; new acceptance_records shape is used as-is).
      const existingGroupValues = material.group_values || material.material?.group_values;
      const { records, passthrough } = parseAcceptanceGroupValues(existingGroupValues);
      this.passthroughGroupValues[msmId] = passthrough;

      // Build concrete groups from material definition
      const groups = material.material?.groups || [];
      this.materialGroups[material.material?.id] = groups;

      this.acceptanceForms[msmId] = {};
      this.groupForms[msmId] = {};

      // Start with one blank record when there is no existing data
      const entries = records.length ? records : [createEmptyAcceptanceRecord()];

      // formData must be set before calculateGroupExpressions runs — it side-effects
      // the form controls based on existing input values, and would zero them out otherwise.
      this.formData[msmId] = entries;

      entries.forEach(record => {
        this.buildRecordForms(material, msmId, record);
        record.groups = this.calculateGroupExpressions(msmId, record.uuid, groups, record.groups);
        this.calculateAcceptanceExpressions(msmId, record.uuid);
      });

      // Auto-expand first material
      if (index === 0) {
        this.expandedMaterialId = material.material?.id;
      }
    });
  }

  private buildRecordForms(material: any, msmId: string, record: AcceptanceRecordEntry) {
    // Build acceptance record form
    const acceptanceFormControls: { [key: string]: FormControl } = {};
    this.acceptanceFields.forEach(field => {
      const existing = record.acceptance_record.find(e => e.id === field.id);
      let value = existing?.value ?? null;

      // Special case: MCL approval comes from source data
      if (field.name === '_mcl_approval' && !value) {
        value = material.material?.source_entity_extra_attributes?.mcl_approval ?? null;
      }

      if (this.getFieldType(field) === 'time') {
        const { timePeriod, amPm, display } = this.parseTimeValue(value || '');
        acceptanceFormControls[field.id] = new FormControl(display || null);
        acceptanceFormControls[`timePeriod_${field.id}`] = new FormControl(timePeriod);
        acceptanceFormControls[`amPm_${field.id}`] = new FormControl(amPm);
      } else {
        acceptanceFormControls[field.id] = new FormControl(value);
      }
    });
    this.acceptanceForms[msmId][record.uuid] = new FormGroup(acceptanceFormControls);

    // Build group forms
    const groups = this.materialGroups[material.material?.id] || [];
    this.groupForms[msmId][record.uuid] = {};
    this.expandedGroupSections[record.uuid] = {};
    groups.forEach(group => {
      (group.fields || []).forEach(field => {
        const existing = record.groups.find(e => e.id === field.id);
        const controls: { [key: string]: FormControl } = {
          value: new FormControl(existing?.value ?? null),
        };
        if (this.getFieldType(field) === 'time') {
          const { timePeriod, amPm, display } = this.parseTimeValue(existing?.value || '');
          controls['value'] = new FormControl(display || null);
          controls[`timePeriod`] = new FormControl(timePeriod);
          controls[`amPm`] = new FormControl(amPm);
        }
        this.groupForms[msmId][record.uuid][field.id] = new FormGroup(controls);
      });
    });
  }

  getMsmId(material: any): string {
    return material.bid_item_material_set_material_id || material.bid_item_material_set_material?.id;
  }

  addRecord(material: any) {
    const msmId = this.getMsmId(material);
    if (!msmId) return;

    const record = createEmptyAcceptanceRecord();
    this.formData[msmId] = [...(this.formData[msmId] || []), record];
    this.buildRecordForms(material, msmId, record);

    const groups = this.materialGroups[material.material?.id] || [];
    record.groups = this.calculateGroupExpressions(msmId, record.uuid, groups, record.groups);
    this.calculateAcceptanceExpressions(msmId, record.uuid);

    // Scroll the new record into view
    setTimeout(() => {
      const sections = this.elRef.nativeElement.closest('.mat-mdc-dialog-surface')
        ?.querySelectorAll('.record-section');
      const last = sections?.[sections.length - 1];
      (last as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  deleteRecord(material: any, recordUuid: string) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        'This acceptance record and its data will be removed if you proceed. Do you want to continue?',
        'Continue',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      const msmId = this.getMsmId(material);
      if (!msmId) return;

      this.formData[msmId] = (this.formData[msmId] || []).filter(r => r.uuid !== recordUuid);
      delete this.acceptanceForms[msmId]?.[recordUuid];
      delete this.groupForms[msmId]?.[recordUuid];
      delete this.expandedGroupSections[recordUuid];
      if (this.scannedFile?.recordUuid === recordUuid) {
        this.scannedFile = null;
      }
    });
  }

  toggleMaterial(materialId: string) {
    this.expandedMaterialId = this.expandedMaterialId === materialId ? null : materialId;
    if (this.expandedMaterialId) {
      setTimeout(() => {
        const accordion = this.elRef.nativeElement.closest('.mat-mdc-dialog-surface')
          ?.querySelector('.material-accordion.is-expanded');
        if (accordion) {
          accordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const firstInput = accordion.querySelector('.material-content input, .material-content textarea, .material-content select');
          (firstInput as HTMLElement)?.focus();
        }
      }, 100);
    }
  }

  toggleGroupSection(recordUuid: string, groupIndex: number, sectionName: string) {
    if (!this.expandedGroupSections[recordUuid]) {
      this.expandedGroupSections[recordUuid] = {};
    }
    const wasExpanded = this.expandedGroupSections[recordUuid][groupIndex] === sectionName;
    this.expandedGroupSections[recordUuid][groupIndex] = wasExpanded ? null : sectionName;
    if (!wasExpanded) {
      setTimeout(() => {
        // Multiple records render their own .group-section elements — scroll/
        // focus only the section that was just toggled, identified by its key.
        const sectionKey = `${recordUuid}:${groupIndex}:${sectionName}`;
        const sections = this.elRef.nativeElement.closest('.mat-mdc-dialog-surface')
          ?.querySelectorAll('.group-section');
        sections?.forEach((section: HTMLElement) => {
          if (section.getAttribute('data-section-key') !== sectionKey) return;
          const content = section.querySelector('.group-section-content');
          if (content) {
            section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            const firstInput = content.querySelector('input, textarea, select');
            (firstInput as HTMLElement)?.focus();
          }
        });
      }, 100);
    }
  }

  isGroupSectionExpanded(recordUuid: string, groupIndex: number, sectionName: string): boolean {
    return this.expandedGroupSections[recordUuid]?.[groupIndex] === sectionName;
  }

  private getRecord(msmId: string, recordUuid: string): AcceptanceRecordEntry {
    return (this.formData[msmId] || []).find(r => r.uuid === recordUuid);
  }

  onAcceptanceFieldChange(msmId: string, recordUuid: string, fieldId: string) {
    const form = this.acceptanceForms[msmId]?.[recordUuid];
    const record = this.getRecord(msmId, recordUuid);
    if (!form || !record) return;

    const arr = record.acceptance_record || [];
    const idx = arr.findIndex(item => item.id === fieldId);
    const value = form.controls[fieldId]?.value;

    if (idx !== -1) {
      arr[idx] = { ...arr[idx], value };
    } else {
      arr.push({ id: fieldId, value });
    }
    record.acceptance_record = arr;

    // Run expression evaluation for acceptance fields
    this.calculateAcceptanceExpressions(msmId, recordUuid);
  }

  private calculateAcceptanceExpressions(msmId: string, recordUuid: string) {
    const fields = this.acceptanceFields;
    const record = this.getRecord(msmId, recordUuid);
    const form = this.acceptanceForms[msmId]?.[recordUuid];
    if (!form || !record) return;
    const arr = record.acceptance_record || [];

    // Build expression map
    const expressionFields = fields.filter(f => f.ui_config?.expression);
    if (expressionFields.length === 0) return;

    // Build data map from current values
    const dataMap: { [name: string]: any } = {};
    fields.forEach(f => {
      const found = arr.find(item => item.id === f.id);
      let val = found?.value ?? null;
      if (val !== null && val !== '' && !isNaN(Number(val)) && f.type === 'number') {
        val = Number(val);
      } else if (f.type === 'number') {
        val = 0;
      }
      if (f.name) dataMap[f.name] = val;
    });

    // Evaluate expressions
    expressionFields.forEach(field => {
      try {
        let result = this.exprEvalService.evaluateExpression(field.ui_config.expression, dataMap);
        if (typeof result === 'number') {
          const precision = field.ui_config?.precision ?? field.precision ?? 5;
          result = Number(result.toFixed(precision));
        }

        // Update formData
        const idx = arr.findIndex(item => item.id === field.id);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], value: result };
        } else {
          arr.push({ id: field.id, value: result });
        }

        // Update form control
        form.controls[field.id]?.setValue(result, { emitEvent: false });

        // Update dataMap for chained expressions
        if (field.name) dataMap[field.name] = result;
      } catch (err) {
        // Expression evaluation failed
      }
    });

    record.acceptance_record = arr;
  }

  isAcceptanceFieldDisplayOnly(field: any): boolean {
    return !!field.display_only || !!field.ui_config?.display_only;
  }

  onGroupFieldChange(msmId: string, recordUuid: string, fieldId: string, groups: any[]) {
    const form = this.groupForms[msmId]?.[recordUuid]?.[fieldId];
    const record = this.getRecord(msmId, recordUuid);
    if (!form || !record) return;

    let arr = record.groups || [];
    const idx = arr.findIndex(item => item.id === fieldId);
    const value = form.controls['value']?.value;

    if (idx !== -1) {
      arr[idx] = { ...arr[idx], value };
    } else {
      arr.push({ id: fieldId, value });
    }

    // Run expression calculation
    arr = this.calculateGroupExpressions(msmId, recordUuid, groups, arr);
    record.groups = arr;
  }

  private calculateGroupExpressions(msmId: string, recordUuid: string, groups: any[], currentValues?: FieldValue[]): FieldValue[] {
    const allFields = groups.flatMap(g => g.fields || []);
    let arr = currentValues || this.getRecord(msmId, recordUuid)?.groups || [];
    const recordGroupForms = this.groupForms[msmId]?.[recordUuid];

    // 1. Handle dependent fields (auto-fill from selected option's extra_attributes)
    allFields.forEach(field => {
      if (!field.ui_config?.dependent_fields) return;
      const fieldVal = arr.find(item => item.id === field.id);
      if (!fieldVal?.value) return;

      const pickListOptions = this.getPickListOptions(field);
      const selectedOption = pickListOptions.find(opt => opt.value === fieldVal.value);
      if (!selectedOption?.extra_attributes) return;

      field.ui_config.dependent_fields.forEach((dep: any) => {
        const targetField = allFields.find(f => f.name === dep.name);
        if (!targetField) return;

        const targetValue = selectedOption.extra_attributes[dep.extra_attribute];
        if (targetValue === undefined) return;

        const targetIdx = arr.findIndex(item => item.id === targetField.id);
        if (targetIdx !== -1) {
          arr[targetIdx] = { ...arr[targetIdx], value: targetValue };
        } else {
          arr.push({ id: targetField.id, value: targetValue });
        }

        // Update form control
        if (recordGroupForms?.[targetField.id]) {
          recordGroupForms[targetField.id].controls['value'].setValue(targetValue, { emitEvent: false });
        }
      });
    });

    // 2. Build expression map
    const expressionMap: { [name: string]: string } = {};
    allFields.forEach(f => {
      if (f.ui_config?.expression && f.name) {
        expressionMap[f.name] = f.ui_config.expression;
      }
    });

    if (Object.keys(expressionMap).length === 0) return arr;

    // Multi-pass evaluation (max 5 iterations)
    let hasChanges = true;
    let iterations = 0;

    while (hasChanges && iterations < 5) {
      hasChanges = false;
      iterations++;

      // Build data map
      const dataMap: { [name: string]: any } = {};
      allFields.forEach(f => {
        const found = arr.find(item => item.id === f.id);
        let val = found?.value ?? null;
        if (val !== null && val !== '' && !isNaN(Number(val)) && f.type === 'number') {
          val = Number(val);
        } else if (f.type === 'number') {
          val = 0;
        }
        if (f.name) dataMap[f.name] = val;
      });


      // Evaluate expressions
      Object.keys(expressionMap).forEach(fieldName => {
        try {
          let result = this.exprEvalService.evaluateExpression(expressionMap[fieldName], dataMap);
          const fieldDef = allFields.find(f => f.name === fieldName);
          if (fieldDef && typeof result === 'number') {
            const precision = fieldDef.ui_config?.precision ?? fieldDef.precision ?? 5;
            result = Number(result.toFixed(precision));
          }
          if (fieldDef) {
            const currentIdx = arr.findIndex(item => item.id === fieldDef.id);
            const currentVal = currentIdx !== -1 ? arr[currentIdx].value : undefined;
            if (currentVal !== result) {
              if (currentIdx !== -1) {
                arr[currentIdx] = { ...arr[currentIdx], value: result };
              } else {
                arr.push({ id: fieldDef.id, value: result });
              }
              // Update the form control for display
              if (recordGroupForms?.[fieldDef.id]) {
                recordGroupForms[fieldDef.id].controls['value'].setValue(result, { emitEvent: false });
              }
              hasChanges = true;
            }
          }
        } catch {
        }
      });
    }


    return arr;
  }

  private convertTimeTo24h(timeValue: string): string {
    if (!timeValue) return timeValue;
    // Already 24h format like "14:58:00"
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeValue)) return timeValue.includes(':') && timeValue.split(':').length === 2 ? `${timeValue}:00` : timeValue;
    // "02:58 PM" format
    const match = timeValue.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2];
      const period = match[3].toUpperCase();
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      return `${String(hour).padStart(2, '0')}:${minute}:00`;
    }
    return timeValue;
  }

  private parseTimeValue(timeValue: string): { timePeriod: string; amPm: string; display: string } {
    if (!timeValue) return { timePeriod: null, amPm: null, display: null };

    // Already formatted: "02:58 PM" or "2:58 am"
    const amPmMatch = timeValue.match(/^(\d{1,2}:\d{2})\s*(am|pm)$/i);
    if (amPmMatch) {
      const tp = amPmMatch[1];
      const ap = amPmMatch[2].toUpperCase();
      return { timePeriod: tp, amPm: ap, display: `${tp} ${ap}` };
    }

    // 24-hour format: "14:58:00" or "02:58:00" or "14:58"
    const h24Match = timeValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (h24Match) {
      let hour = parseInt(h24Match[1], 10);
      const minute = h24Match[2];
      const ap = hour >= 12 ? 'PM' : 'AM';
      if (hour === 0) hour = 12;
      else if (hour > 12) hour -= 12;
      const tp = `${String(hour).padStart(2, '0')}:${minute}`;
      return { timePeriod: tp, amPm: ap, display: `${tp} ${ap}` };
    }

    return { timePeriod: timeValue, amPm: null, display: timeValue };
  }

  getFieldType(field: any): string {
    if (field.ui_config?.display_only || field.display_only) return 'display_only';
    if (field.ui_config?.expression || field.ui_config?.expression_name || field.ui_config?.value_expression) return 'display_only';
    if (field.componentType === 'dropdown') return 'dropdown';
    if (Array.isArray(field.ui_config?.pick_list) && field.ui_config.pick_list.length > 0) return 'dropdown';
    if (Array.isArray(field.ui_config?.pick_list_values) && field.ui_config.pick_list_values.length > 0) return 'dropdown';
    if (typeof field.ui_config?.pick_list === 'string' && field.ui_config.pick_list) return 'dropdown';
    if (field.componentType === 'date' || field.type === 'date') return 'date';
    if (field.componentType === 'time' || field.type === 'time') return 'time';
    if (field.componentType === 'boolean' || field.type === 'boolean') return 'boolean';
    if (field.componentType === 'number' || field.type === 'number') return 'number';
    return 'text';
  }

  getPickListOptions(field: any): any[] {
    if (this.pickListOptionsCache[field.id]) {
      return this.pickListOptionsCache[field.id];
    }

    let options: any[] = [];

    // Inline pick_list array
    if (Array.isArray(field.ui_config?.pick_list) && field.ui_config.pick_list.length > 0) {
      options = field.ui_config.pick_list.map(item => ({
        value: typeof item === 'string' ? item : item.value,
        label: typeof item === 'string' ? item : (item.label || item.name || item.value),
      }));
    }

    // Inline pick_list_values array
    else if (Array.isArray(field.ui_config?.pick_list_values) && field.ui_config.pick_list_values.length > 0) {
      options = field.ui_config.pick_list_values.map(item => ({
        value: typeof item === 'string' ? item : (item.value ?? item),
        label: typeof item === 'string' ? item : (item.label || item.name || item.value),
      }));
    }

    // String reference to lookup (e.g. "truck_id.value")
    else {
      const pickList = field.ui_config?.pick_list || field.pick_list;
      if (typeof pickList === 'string') {
        const lookupType = pickList.split('.')?.[0];
        const projectLookupData = this.projectLookups?.[lookupType] || [];
        const appDataLookupData = this.appData?.lookups?.[lookupType] || [];
        const lookupData = projectLookupData.length > 0 ? projectLookupData : appDataLookupData;
        options = lookupData.map(item => ({
          value: item.value ?? item.id ?? item,
          label: item.label || item.name || item.value || String(item),
          ...(item.extra_attributes ? { extra_attributes: item.extra_attributes } : {}),
        }));
      }
    }

    this.pickListOptionsCache[field.id] = options;
    return options;
  }

  formatSectionName(section: any): string {
    if (section.label) return section.label;
    const name = section.name || '';
    const formatted = name.replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  getSortedSections(group: any): any[] {
    const sections = group.ui_config?.sections || [];
    return [...sections].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }

  getSectionFields(group: any, sectionName: string): any[] {
    return (group.fields || [])
      .filter(f => f.ui_config?.section_name === sectionName)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }

  getAcceptanceFieldValue(msmId: string, recordUuid: string, fieldId: string): any {
    return this.acceptanceForms[msmId]?.[recordUuid]?.controls[fieldId]?.value;
  }

  getGroupFieldValue(msmId: string, recordUuid: string, fieldId: string): any {
    return this.groupForms[msmId]?.[recordUuid]?.[fieldId]?.controls['value']?.value;
  }

  isSectionAutofilled(recordUuid: string, sectionName: string): boolean {
    return this.autofilledSections.has(`${recordUuid}:${sectionName}`);
  }

  /**
   * Applies the edits back to the line item and closes. Also runs when the
   * per-material Samples list opens the sample panel: the panel lives under
   * this modal, so the dialog hands its state back first instead of leaving
   * the panel unreachable behind the backdrop.
   */
  done() {
    // Build updated materials with group_values in the new multi-record shape
    const updatedMaterials = this.materials.map(material => {
      const msmId = material.bid_item_material_set_material_id || material.bid_item_material_set_material?.id;
      if (!msmId || !this.formData[msmId]) return material;

      const groups = this.materialGroups[material.material?.id] || [];
      const allGroupFields = groups.flatMap(g => g.fields || []);

      const acceptanceRecords = this.formData[msmId]
        .map(record => {
          // Collect acceptance record values from the record's form
          const acceptanceRecord: FieldValue[] = [];
          this.acceptanceFields.forEach(field => {
            let value = this.acceptanceForms[msmId]?.[record.uuid]?.controls[field.id]?.value;
            if (value != null && value !== '') {
              if (this.getFieldType(field) === 'time') {
                value = this.convertTimeTo24h(value);
              }
              acceptanceRecord.push({ id: field.id, value });
            }
          });

          // Collect group values, filtering out display_only and converting time fields
          const groupValues: FieldValue[] = (record.groups || [])
            .filter(gv => {
              const fieldDef = allGroupFields.find(f => f.id === gv.id);
              return !fieldDef?.ui_config?.display_only && !fieldDef?.display_only;
            })
            .map(gv => {
              const fieldDef = allGroupFields.find(f => f.id === gv.id);
              if (fieldDef && this.getFieldType(fieldDef) === 'time') {
                const formValue = this.groupForms[msmId]?.[record.uuid]?.[gv.id]?.controls['value']?.value;
                return { ...gv, value: this.convertTimeTo24h(formValue || gv.value) };
              }
              return gv;
            });

          return {
            uuid: record.uuid,
            acceptance_record: acceptanceRecord,
            groups: groupValues,
          };
        })
        // Entries with no acceptance-record AND no group data are empty — don't
        // persist. Orgs whose data lives only in concrete groups (e.g. batch
        // ticket scans with no material_acceptance fields) must still be kept.
        .filter(record => record.acceptance_record.length > 0 || record.groups.length > 0);

      // New multi-record shape; any other group_values keys pass through
      // untouched. The legacy top-level acceptance_record/groups keys are ALSO
      // mirrored from the first record for mobile back-compat: mobile currently
      // reads only the legacy keys and rebuilds group_values from them on edit,
      // so omitting them would wipe web-entered data. The api's
      // dwr-acceptance-records-sync normalizeAcceptanceRecords prefers
      // acceptance_records when present, so the mirror is safe server-side.
      // Remove the mirror once mobile ships the multi-record reader.
      const groupValuesObj = {
        ...(this.passthroughGroupValues[msmId] || {}),
        acceptance_records: acceptanceRecords,
        acceptance_record: acceptanceRecords[0]?.acceptance_record || [],
        groups: acceptanceRecords[0]?.groups || [],
      };

      return {
        ...material,
        group_values: groupValuesObj,
        material: {
          ...material.material,
          group_values: groupValuesObj,
        },
      };
    });

    this.dialogRef.close({ materials: updatedMaterials });
  }

  deleteAcceptanceRecord() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        'The acceptance record data will be removed if you proceed. Do you want to continue?',
        'Continue',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ deleted: true });
      }
    });
  }

  hasExistingData(): boolean {
    return this.materials.some(m => {
      const { records } = parseAcceptanceGroupValues(m.group_values || m.material?.group_values);
      return records.some(acceptanceRecordHasData);
    });
  }

  close() {
    this.dialogRef.close();
  }

  // --- Scan Batch Ticket ---

  get hasConcreteGroups(): boolean {
    return this.materials.some(m => (this.materialGroups[m.material?.id] || []).length > 0);
  }

  // The scan spinner belongs only to the record whose scan was triggered —
  // batchTicketScanning alone is component-wide and would render on every record.
  isScanningRecord(recordUuid: string): boolean {
    return this.batchTicketScanning && this.batchTicketTarget?.recordUuid === recordUuid;
  }

  triggerBatchTicketUpload(msmId: string, recordUuid: string) {
    this.batchTicketTarget = { msmId, recordUuid };
    this.batchTicketFileInput?.nativeElement?.click();
  }

  onBatchTicketFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    this.store.dispatch(ScanBatchTicketRequest({ payload: { file } }));
    input.value = '';
  }

  private populateExtractedData(extractedData: any) {
    const BATCH_TICKET_FIELD_MAPPING = [
      'pour_number', 'load_size_cy', 'ca_lbs', 'ia_lbs', 'fa_lbs',
      'portland_cement', 'fly_ash', 'microsilica', 'slag',
      'water_from_batch_ticket_gal', 'water_added_on_site_gal',
      'ca_moisture_bt', 'ia_moisture_bt', 'fa_moisture_bt', 'mix_design',
    ];

    const data = extractedData?.extracted_data || extractedData;
    const flatData = {
      ...(data?.pour_information || {}),
      ...(data?.batch_masses || {}),
      ...(data?.aggregate_moisture_corrections || {}),
      ...(data?.summary || {}),
    };

    // Scans target the record whose "Scan batch ticket" action was clicked
    const target = this.batchTicketTarget;
    if (!target) {
      this.store.dispatch(ClearBatchTicketData());
      return;
    }
    this.batchTicketTarget = null;

    const material = this.materials.find(m => this.getMsmId(m) === target.msmId);
    const record = this.getRecord(target.msmId, target.recordUuid);
    if (!material || !record) {
      this.store.dispatch(ClearBatchTicketData());
      return;
    }

    const groups = this.materialGroups[material.material?.id] || [];
    const allFields = groups.flatMap(g => g.fields || []);
    const recordGroupForms = this.groupForms[target.msmId]?.[target.recordUuid] || {};

    // A fresh scan replaces the record's autofill state — clear its stale
    // "Autofilled" badges so a re-scan touching fewer sections doesn't keep
    // badges from the previous scan.
    this.autofilledSections.forEach(key => {
      if (key.startsWith(`${target.recordUuid}:`)) {
        this.autofilledSections.delete(key);
      }
    });

    let totalFieldsUpdated = 0;

    BATCH_TICKET_FIELD_MAPPING.forEach(fieldName => {
      const apiValue = flatData?.[fieldName];
      if (apiValue === undefined || apiValue === null) return;

      const fieldDef = allFields.find(f => f.name === fieldName);
      if (!fieldDef) return;

      const valueToSet = fieldDef.type === 'number' ? String(apiValue) : apiValue;

      // Update form control
      if (recordGroupForms[fieldDef.id]) {
        recordGroupForms[fieldDef.id].controls['value']?.setValue(valueToSet, { emitEvent: false });
        totalFieldsUpdated++;
      }

      // Update formData
      const arr = record.groups || [];
      const idx = arr.findIndex(item => item.id === fieldDef.id);
      if (idx !== -1) {
        arr[idx] = { ...arr[idx], value: valueToSet };
      } else {
        arr.push({ id: fieldDef.id, value: valueToSet });
      }
      record.groups = arr;
    });

    // Recalculate computed fields
    if (groups.length) {
      record.groups = this.calculateGroupExpressions(target.msmId, target.recordUuid, groups, record.groups);
    }

    // Track which sections of the target record were autofilled
    const extractedFieldNames = Object.keys(flatData).filter(k => flatData[k] !== undefined && flatData[k] !== null);
    extractedFieldNames.forEach(fieldName => {
      const fieldDef = allFields.find(f => f.name === fieldName);
      const sectionName = fieldDef?.ui_config?.section_name;
      if (sectionName) this.autofilledSections.add(`${target.recordUuid}:${sectionName}`);
    });

    this.scannedFile = {
      name: 'Batch ticket',
      fieldsAutofilled: totalFieldsUpdated,
      recordUuid: target.recordUuid,
    };

    // Auto-expand the target record's first sections
    groups.forEach((group, gi) => {
      const sections = this.getSortedSections(group);
      if (sections.length) {
        this.expandedGroupSections[target.recordUuid] = this.expandedGroupSections[target.recordUuid] || {};
        this.expandedGroupSections[target.recordUuid][gi] = sections[0].name;
      }
    });

    this.store.dispatch(ClearBatchTicketData());
  }

  ngOnDestroy() {
    this.store.dispatch(ClearBatchTicketData());
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
